/**
 * Support portal link API: the per-customer portal-access view + the
 * per-contact actions (grant / revoke / resend invitation) the customer
 * widget uses.
 *
 * The CRM enqueues a PendingPortalAction; the support portal pulls the
 * queue over its own signed service token the moment crm-api nudges it
 * (SupportPortal::Nudge, 2026-09-14) and every ten minutes as a reconcile.
 * The old read-only "cockpit" client (sync health / action list / retry)
 * went with the Portal Access Queue page on 2026-09-12 and is deleted.
 */
import api from './api';

export interface PendingActionRow {
  id: number;
  action: 'grant' | 'revoke' | 'reissue';
  status: 'pending' | 'completed' | 'failed';
  organisation_external_id: string;
  contact_external_id: string;
  contact_email: string | null;
  contact_name: string | null;
  requested_at: string;
  acked_at: string | null;
  attempts: number;
  last_error: string | null;
  requested_by: { id?: number; name?: string; email?: string };
  full_payload?: Record<string, unknown>;
}

export interface PortalAccessState {
  access_state: 'none' | 'invited' | 'active' | 'revoked';
  invited_at: string | null;
  accepted_at: string | null;
  revoked_at: string | null;
  last_login_at: string | null;
  invitation_sent_count: number;
}

export interface CustomerSyncDetail {
  customer_id: number;
  last_synced_at: string | null;
  pending_count: number;
  actions: PendingActionRow[];
  contacts: Array<{
    id: number;
    full_name: string;
    email: string;
    job_title: string | null;
    portal: PortalAccessState | { access_state: 'none' };
    pending_action: { id: number; action: string; requested_at: string } | null;
  }>;
}

export async function fetchCustomerSyncDetail(customerId: number | string): Promise<CustomerSyncDetail> {
  const res = await api.get(`/admin/support_sync/customers/${customerId}`);
  return res.data.data;
}

// ── Per-contact portal access actions ─────────────────────────────

export async function grantPortalAccess(contactId: number | string) {
  const res = await api.post(`/contacts/${contactId}/portal_access`);
  return res.data as { ok: boolean; status: string; state: PortalAccessState };
}

export async function revokePortalAccess(contactId: number | string) {
  const res = await api.delete(`/contacts/${contactId}/portal_access`);
  return res.data as { ok: boolean; status: string; state: PortalAccessState };
}

export async function resendPortalInvitation(contactId: number | string) {
  const res = await api.post(`/contacts/${contactId}/portal_access/resend`);
  return res.data as { ok: boolean; status: string; state: PortalAccessState };
}
